"use client";

import { cn } from "@/client/lib/classnames";
import { SOCIAL_LINKS } from "./constants";
import { ArrowRightIcon, XIcon } from "./icons";
import { AnimatePresence, motion, Variants } from "motion/react";
import React from "react";
import { NAV_ITEMS, NavItem } from "./constants";
import * as Dialog from "@radix-ui/react-dialog";
import { ArtBlocksWordmarkLockupFullColor } from "@/client/components/icons/ab-logo";
import Link from "next/link";
import { getBasePath } from "@/shared/config";

type ResponsiveMenuProps = {
  onDismiss: () => void;
  visible: boolean;
};

export const ResponsiveMenu = ({
  onDismiss,
  visible,
}: ResponsiveMenuProps): React.ReactElement => {
  const [activeSubmenu, setActiveSubmenu] = React.useState<NavItem[] | null>(
    null
  );
  // This will lag one render behind and allow us to control the main menu animation
  // based on whether we're initially viewing the menu or if we're returning from a submenu
  const [previousActiveSubmenu, setPreviousActiveSubmenu] = React.useState<
    NavItem[] | null
  >(null);
  React.useEffect(() => {
    setPreviousActiveSubmenu(activeSubmenu);
  }, [activeSubmenu]);

  const responsiveMenuItemClasses =
    "gap-sm p-md flex w-full items-center font-medium";

  const mainMenuAnimationVariants: Variants = {
    initial: (previousActiveSubmenu: NavItem[] | null | undefined) => {
      if (previousActiveSubmenu) {
        return {
          x: "-100%",
          opacity: 0,
        };
      }

      return {
        x: 0,
        opacity: 1,
      };
    },
    animate: {
      x: 0,
      opacity: 1,
    },
    exit: {
      x: "-100%",
      opacity: 0,
    },
  };

  return (
    <ResponsiveMenuDialog
      visible={visible}
      onDismiss={onDismiss}
      containerClassName="flex flex-1 flex-col h-full overflow-x-hidden relative"
    >
      <div className="relative flex-1">
        <AnimatePresence>
          {!activeSubmenu ? (
            <motion.div
              key="main-menu"
              className="absolute inset-0 flex flex-col items-start overflow-auto [justify-content:safe_center]"
              variants={mainMenuAnimationVariants}
              custom={previousActiveSubmenu}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{
                visualDuration: 0.5,
                bounce: 0,
              }}
            >
              {NAV_ITEMS.map(({ label, value, subItems, href }) => {
                // Special case discord, because we show all social icons
                // below the main menu items
                if (value === "discord") {
                  return null;
                }

                if (!subItems) {
                  return (
                    <Link
                      key={value}
                      href={href ?? ""}
                      className={cn(responsiveMenuItemClasses, "text-display")}
                      onClick={() => {
                        onDismiss();
                      }}
                    >
                      {label}
                    </Link>
                  );
                }

                return (
                  <button
                    key={value}
                    className={cn(responsiveMenuItemClasses, "text-display")}
                    onClick={() => {
                      setActiveSubmenu(subItems);
                    }}
                  >
                    <span>{label}</span>
                    <ArrowRightIcon className="w-md h-md fill-on-surface" />
                  </button>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key="submenu"
              className="absolute inset-0 flex flex-col items-start overflow-auto [justify-content:safe_center]"
              initial={{
                x: "100%",
                opacity: 0,
              }}
              animate={{
                x: 0,
                opacity: 1,
              }}
              exit={{
                x: "100%",
                opacity: 0,
              }}
              transition={{
                visualDuration: 0.4,
                bounce: 0,
              }}
            >
              <button
                className={cn(
                  responsiveMenuItemClasses,
                  "text-on-surface-variant text-display"
                )}
                onClick={() => setActiveSubmenu(null)}
              >
                <ArrowRightIcon className="rotate-180 w-md h-md fill-on-surface-variant" />
                <span>Back</span>
              </button>
              {activeSubmenu?.map(({ label, value, href }) => {
                return (
                  <Link
                    key={value}
                    href={href ?? ""}
                    className={cn(responsiveMenuItemClasses, "text-display")}
                    onClick={() => {
                      onDismiss();
                    }}
                  >
                    {label}
                  </Link>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <motion.div
        layout
        className="flex flex-row items-center justify-center gap-x-md p-2xl"
      >
        {SOCIAL_LINKS.map(({ icon: Icon, href }) => (
          <SocialLink href={href} key={href}>
            <Icon className="fill-on-surface h-md w-md" />
          </SocialLink>
        ))}
      </motion.div>
    </ResponsiveMenuDialog>
  );
};

const SocialLink = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => {
  return (
    <a href={href} target="_blank" rel="norefferer">
      {children}
    </a>
  );
};

type ResponsiveMenuDialogProps = {
  children: React.ReactNode;
  onDismiss: () => void;
  visible: boolean;
  containerClassName?: string;
};

export function ResponsiveMenuDialog({
  children,
  onDismiss,
  visible,
  containerClassName,
}: ResponsiveMenuDialogProps): React.ReactNode {
  return (
    <Dialog.Root open={visible} onOpenChange={onDismiss}>
      <AnimatePresence mode="wait">
        {visible ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-surface/90 fixed inset-0 top-0 z-[60] flex items-start justify-center overflow-y-auto backdrop-blur-[25px]"
              >
                <Dialog.Content
                  className={`w-full bg-transparent bg-opacity-100 ${containerClassName ? containerClassName : ""}`}
                >
                  <div className="flex flex-row items-center justify-between w-full p-md">
                    <Link href={getBasePath()} onClick={() => onDismiss()}>
                      <ArtBlocksWordmarkLockupFullColor className="fill-on-surface h-[30px]" />
                    </Link>
                    <button onClick={() => onDismiss()}>
                      <XIcon className="fill-on-surface w-sm h-sm" />
                    </button>
                  </div>
                  {children}
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>
        ) : undefined}
      </AnimatePresence>
    </Dialog.Root>
  );
}
